import React, { FC } from 'react'
import { ItemProps } from '../../types'
import CodeMirror, { ReactCodeMirrorProps } from '@uiw/react-codemirror'
import { EditorView } from '@codemirror/view'
import { Extension } from '@codemirror/state'
import { css } from '@codemirror/lang-css'
import { javascript } from '@codemirror/lang-javascript'
import { html } from '@codemirror/lang-html'
import { python } from '@codemirror/lang-python'
import ValidationError from '@/components/Form/elements/ValidationError'

// Helper function to map script type strings to CodeMirror extensions
const getScriptExtension = (script: string): Extension[] => {
	switch (script) {
		case 'javascript':
		case 'typescript':
			return [javascript({ typescript: script === 'typescript', jsx: true })]
		case 'css':
			return [css()]
		case 'html':
			return [html()]
		case 'python':
			return [python()]
		case 'markdown':
			return [python()]
		default:
			return []
	}
}

const Code: FC<ItemProps> = (
	{
		formItem,
		validation,
		disabled,
		formData,
		onChange,
		onFocus,
		onBlur,
		onKeyDown,
		autoFocus,
	},
) => {


	// Handle changes in CodeMirror editor
	const handleCodeChange: ReactCodeMirrorProps['onChange'] = (value: string) => {
		onChange?.(formItem.name, value)
	}


	// Define editor theme with specific styles
	const editorTheme = EditorView.theme({
		'&': {
			transition: 'all 0.2s ease-in-out', // transition-all
			outline: 'none !important', // outline-none
			width: '100%', // w-full
			fontSize: '14px', // text-md (from text-md in tailwind config)
			lineHeight: '21px', // lineHeight for `text-md`
			color: '#1f2937', // text-gray-900
			borderRadius: '8px', // rounded-md from your `borderRadius.md`
			border: '1px solid #d1d5db', // border border-gray-300
			backgroundColor: '#ffffff', // bg-white
			padding: '0', // px-md and py-2
			overflow: 'hidden', // overflow-hidden
		},
		'&.cm-focused': {
			boxShadow: '0 0 0 4px rgba(121, 188, 97, 0.25)', // focus:shadow-focus
			borderColor: '#79BC61', // focus:border-primary-500 from primary.500
		},
		'&:hover': {
			borderColor: '#79BC61', // hover:border-primary-500 or gray.hover
		},
		'.cm-content': {
			caretColor: '#1f2937', // text color for editor
		},
		'.cm-placeholder': {
			color: '#9ca3af', // placeholder:text-gray-400
			fontSize: '14px', // placeholder:text-md
		},
		'.cm-gutters': {
			backgroundColor: '#FAFAFA', // Match editor background
			borderColor: '#EBECEB', // border-gray-300
			color: '#9EA49C', // text-gray-500
			transition: 'all 0.2s ease-in-out', // transition-all
			paddingLeft: '8px', // gutter padding
			//border: "none",
		},
		'.cm-lineNumbers .cm-gutterElement:nth-child(2)': {
			paddingTop: '8px',
		},
		'.cm-lineNumbers .cm-gutterElement:last-child': {
			paddingBottom: '8px',
		},
		'.cm-foldGutter': {
			padding: '0 8px',
			color: '#141A12',
		},
		'.cm-foldGutter .cm-gutterElement:nth-child(2)': {
			paddingTop: '8px',
		},
		'.cm-foldGutter .cm-gutterElement:last-child': {
			paddingBottom: '8px',

		},

		'&.cm-focused .cm-gutters': {
			backgroundColor: '#EBF4E8', // Match editor background
			borderColor: '#C2E1B7', // border-gray-300
			color: '#69A455', // focus:border-primary-500
			//border: "none",
		},
		'.cm-line:first-child': {
			paddingTop: '8px', // Reset padding for first line
		},
		'.cm-line:last-child': {
			paddingBottom: '8px', // Reset padding for first line
		},
		'.cm-line': {
			padding: '0 16px', // Reset padding for code lines
		},
		'&.cm-focused .cm-activeLine': {
			backgroundColor: 'rgba(20, 26, 18, 0.04)', // Remove any default highlighting for active line
		},
		'.cm-activeLine': {
			backgroundColor: 'transparent', // Remove any default highlighting for active line
		},
		'.cm-activeLineGutter': {
			backgroundColor: 'transparent', // Remove any gutter highlighting for active line
		},
		'& .cm-scroller': {
			//boxShadow: "0px 1px 3px -2px rgba(16, 24, 40, 0.06), 0px 2px 4px -1px rgba(16, 24, 40, 0.1)", // shadow-xs
		},
		'&.cm-focused .cm-selectionBackground, ::selection': {
			backgroundColor: '#C2E1B7 !important',
		},
		'.cm-selectionBackground, ::selection': {
			backgroundColor: 'transparent !important',
		},
		'.cm-cursor': {
			borderColor: '#79BC61 !important',
			borderLeftWidth: '3px',
		},
	})


	// Determine extensions based on script type or fallback
	const extensions: Extension[] = [
		...(formItem.itemOptions?.script ? getScriptExtension(formItem.itemOptions.script) : []),
		EditorView.lineWrapping,
		editorTheme,
	]

	const rawOptions = (formItem.itemOptions || {}) as any
	const { onClick: _ignoredOnClick, ...codeMirrorOptions } = rawOptions


	return (
		<>

			<CodeMirror
				value={formData[formItem.name] || ''}
				extensions={extensions}
				onChange={handleCodeChange}
				onFocus={onFocus as React.FocusEventHandler}
				onBlur={onBlur as React.FocusEventHandler}
				onKeyDown={onKeyDown as React.KeyboardEventHandler}
				autoFocus={autoFocus}
				editable={!disabled}
				aria-rowcount={formItem.itemOptions?.rows || 10}
				className={`transition-all outline-none
					${disabled ? 'disabled' : ''}
					w-full text-text-md text-gray-900 rounded-md 1border border-gray-300 bg-white 
					placeholder:text-gray-400 placeholder:text-text-md
					focus:shadow-focus focus:border-primary-500
					hover:border-primary-500 shadow-xs
					${formItem.classNames?.content || ''}
					${validation ? 'border-red-500 border-2 focus:border-red-600 focus:shadow-red' : ''}
				`}
				{...codeMirrorOptions}
			/>
			<ValidationError error={validation} formItem={formItem} />
		</>
	)
}

export default Code
